"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo, useState } from "react";
import FormSection from "../ui/FormSection";
import MetadataLengthHint from "../ui/MetadataLengthHint";
import { TraitWrapper } from "../../traits/TraitWrapper";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { type AirdropEntry, AIRDROP_TOTAL } from "../types/OperationalData";
import { validateStrictAddress } from "../utils/addressValidation";
import type { MetadataValueLengthStatus } from "../utils/submissionMetadata";
import EnsAddressInput from "@/components/utils/input/ens-address/EnsAddressInput";
interface AirdropConfigProps {
  readonly entries: AirdropEntry[];
  readonly onEntriesChange: (entries: AirdropEntry[]) => void;
  readonly airdropLengthStatus?: MetadataValueLengthStatus | undefined;
}
const AirdropConfig: React.FC<AirdropConfigProps> = t0 => {
  const $ = _c(73);
  const {
    entries,
    onEntriesChange,
    airdropLengthStatus
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [loadingStates, setLoadingStates] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [ensErrorStates, setEnsErrorStates] = useState(t2);
  const totalAllocated = entries.reduce(_temp, 0);
  const remaining = AIRDROP_TOTAL - totalAllocated;
  const firstEntry = entries[0];
  let t3;
  if ($[2] !== ensErrorStates) {
    t3 = (entryId, address) => {
      if (ensErrorStates[entryId]) {
        return "Could not resolve ENS name";
      }
      if (!address) {
        return null;
      }
      return validateStrictAddress(address) ? null : "Invalid address (0x... 42 chars)";
    };
    $[2] = ensErrorStates;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const getAddressError = t3;
  let t4;
  if ($[4] !== entries || $[5] !== onEntriesChange || $[6] !== remaining) {
    t4 = () => {
      const defaultCount = Math.max(0, remaining);
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      onEntriesChange([...entries, {
        id,
        address: "",
        count: defaultCount
      }]);
    };
    $[4] = entries;
    $[5] = onEntriesChange;
    $[6] = remaining;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleAddEntry = t4;
  let t5;
  if ($[8] !== entries || $[9] !== onEntriesChange) {
    t5 = index => {
      if (entries.length <= 1) {
        return;
      }
      const entryToRemove = entries[index];
      const newEntries = [...entries];
      newEntries.splice(index, 1);
      onEntriesChange(newEntries);
      if (entryToRemove) {
        setLoadingStates(prev => {
          const next = {
            ...prev
          };
          delete next[entryToRemove.id];
          return next;
        });
        setEnsErrorStates(prev_0 => {
          const next_0 = {
            ...prev_0
          };
          delete next_0[entryToRemove.id];
          return next_0;
        });
      }
    };
    $[8] = entries;
    $[9] = onEntriesChange;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleRemoveEntry = t5;
  let t6;
  if ($[11] !== entries || $[12] !== onEntriesChange) {
    t6 = (index_0, resolvedAddress) => {
      const newEntries_0 = [...entries];
      const currentEntry = newEntries_0[index_0];
      if (!currentEntry) {
        return;
      }
      newEntries_0[index_0] = {
        ...currentEntry,
        address: resolvedAddress
      };
      onEntriesChange(newEntries_0);
    };
    $[11] = entries;
    $[12] = onEntriesChange;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleAddressChange = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (entryId_0, isLoading) => {
      setLoadingStates(prev_1 => ({
        ...prev_1,
        [entryId_0]: isLoading
      }));
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleLoadingChange = t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (entryId_1, hasError) => {
      setEnsErrorStates(prev_2 => ({
        ...prev_2,
        [entryId_1]: hasError
      }));
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleEnsError = t8;
  let t9;
  if ($[16] !== entries || $[17] !== onEntriesChange) {
    t9 = (index_1, value) => {
      const newEntries_1 = [...entries];
      const currentEntry_0 = newEntries_1[index_1];
      if (!currentEntry_0) {
        return;
      }
      const numVal = Number.parseInt(value, 10);
      newEntries_1[index_1] = {
        ...currentEntry_0,
        count: Number.isNaN(numVal) ? 0 : Math.max(0, numVal)
      };
      onEntriesChange(newEntries_1);
    };
    $[16] = entries;
    $[17] = onEntriesChange;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleCountChange = t9;
  const isOverAllocated = totalAllocated > AIRDROP_TOTAL;
  const isUnderAllocated = totalAllocated < AIRDROP_TOTAL;
  const isExactlyAllocated = totalAllocated === AIRDROP_TOTAL;
  let t10;
  if ($[19] !== isExactlyAllocated || $[20] !== isOverAllocated) {
    t10 = () => {
      if (isExactlyAllocated) {
        return "tw-border tw-border-[#0d402b] tw-bg-[#062417]";
      }
      if (isOverAllocated) {
        return "tw-bg-red/10 tw-border tw-border-red/30";
      }
      return "tw-border tw-border-iron-800 tw-bg-iron-900/70";
    };
    $[19] = isExactlyAllocated;
    $[20] = isOverAllocated;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const getSummaryBarClasses = t10;
  let t11;
  if ($[22] !== isExactlyAllocated || $[23] !== isOverAllocated) {
    t11 = () => {
      if (isOverAllocated) {
        return "tw-text-red";
      }
      if (isExactlyAllocated) {
        return "tw-text-emerald-500";
      }
      return "tw-text-iron-100";
    };
    $[22] = isExactlyAllocated;
    $[23] = isOverAllocated;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const getTotalTextColorClass = t11;
  const t12 = `tw-mb-6 tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-rounded-lg tw-border-solid tw-px-4 tw-py-3 ${getSummaryBarClasses()}`;
  const t13 = `tw-px-0.5 tw-font-bold ${getTotalTextColorClass()}`;
  let t14;
  if ($[25] !== t13 || $[26] !== totalAllocated) {
    t14 = <span className="tw-text-[13px] tw-font-medium tw-text-iron-400">Total allocated:{" "}<span className={t13}>{totalAllocated}</span>{" "}/ {AIRDROP_TOTAL}</span>;
    $[25] = t13;
    $[26] = totalAllocated;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== isOverAllocated || $[29] !== remaining) {
    t15 = isOverAllocated && <span className="tw-text-[13px] tw-font-semibold tw-text-red">{Math.abs(remaining)} over limit</span>;
    $[28] = isOverAllocated;
    $[29] = remaining;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== isUnderAllocated || $[32] !== remaining) {
    t16 = isUnderAllocated && <span className="tw-text-[13px] tw-font-medium tw-text-iron-400">{remaining} remaining</span>;
    $[31] = isUnderAllocated;
    $[32] = remaining;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== isExactlyAllocated) {
    t17 = isExactlyAllocated && <span className="tw-text-[13px] tw-font-semibold tw-text-emerald-500">Complete</span>;
    $[34] = isExactlyAllocated;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t12 || $[37] !== t14 || $[38] !== t15 || $[39] !== t16 || $[40] !== t17) {
    t18 = <div className={t12}>{t14}{t15}{t16}{t17}</div>;
    $[36] = t12;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== airdropLengthStatus) {
    t19 = <MetadataLengthHint status={airdropLengthStatus} className="tw-mb-4" />;
    $[42] = airdropLengthStatus;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== entries || $[45] !== getAddressError || $[46] !== handleAddressChange || $[47] !== handleCountChange || $[48] !== handleRemoveEntry || $[49] !== loadingStates) {
    let t21;
    if ($[51] !== entries.length || $[52] !== getAddressError || $[53] !== handleAddressChange || $[54] !== handleCountChange || $[55] !== handleRemoveEntry || $[56] !== loadingStates) {
      t21 = (entry, index_2) => {
        const error = getAddressError(entry.id, entry.address);
        const isLoading_0 = loadingStates[entry.id] ?? false;
        const isValid = !!entry.address && !error && !isLoading_0;
        return <div key={entry.id} className="tw-flex tw-items-start tw-gap-x-4"><TraitWrapper label="Wallet Address" id={`airdrop-${index_2}-address`} error={error} isFieldFilled={isValid} labelTone="muted" className="tw-flex-1 tw-pb-0"><EnsAddressInput value={entry.address} placeholder="0x... or ENS" onAddressChange={addr => handleAddressChange(index_2, addr)} onLoadingChange={loading => handleLoadingChange(entry.id, loading)} onError={hasError_0 => handleEnsError(entry.id, hasError_0)} className={`tw-form-input tw-w-full tw-truncate tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-3 tw-pl-4 tw-pr-11 tw-text-base tw-text-iron-100 tw-outline-none tw-ring-1 focus:tw-bg-iron-900 focus:tw-text-iron-100 sm:tw-text-sm ${error ? "tw-ring-red" : "tw-ring-iron-700"} focus:tw-ring-primary-400`} /></TraitWrapper><TraitWrapper label="Count" id={`airdrop-${index_2}-count`} isFieldFilled={!!entry.count} labelTone="muted" className="tw-w-[120px] tw-pb-0"><input type="number" min="0" max={AIRDROP_TOTAL} value={entry.count || ""} onChange={e_0 => handleCountChange(index_2, e_0.target.value)} className="tw-form-input tw-w-full tw-truncate tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-3 tw-pl-4 tw-pr-11 tw-text-base tw-text-iron-100 tw-outline-none tw-ring-1 tw-ring-iron-700 focus:tw-ring-primary-400 sm:tw-text-sm" /></TraitWrapper>{entries.length > 1 && <button type="button" onClick={() => handleRemoveEntry(index_2)} className="-tw-ml-2 tw-flex tw-cursor-pointer tw-items-center tw-self-center tw-border-0 tw-bg-transparent tw-p-2 tw-text-iron-400 tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-rose-400" aria-label={`Remove address ${index_2 + 1}`}><TrashIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" /></button>}</div>;
      };
      $[51] = entries.length;
      $[52] = getAddressError;
      $[53] = handleAddressChange;
      $[54] = handleCountChange;
      $[55] = handleRemoveEntry;
      $[56] = loadingStates;
      $[57] = t21;
    } else {
      t21 = $[57];
    }
    t20 = entries.map(t21);
    $[44] = entries;
    $[45] = getAddressError;
    $[46] = handleAddressChange;
    $[47] = handleCountChange;
    $[48] = handleRemoveEntry;
    $[49] = loadingStates;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[58] !== t20) {
    t21 = <div className="tw-flex tw-flex-col tw-gap-y-6">{t20}</div>;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  let t23;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <PlusIcon className="tw-size-4 tw-flex-shrink-0" />;
    t23 = <span>Add Address</span>;
    $[60] = t22;
    $[61] = t23;
  } else {
    t22 = $[60];
    t23 = $[61];
  }
  let t24;
  if ($[62] !== handleAddEntry) {
    t24 = <button type="button" onClick={handleAddEntry} className="-tw-ml-2 tw-mt-3 tw-flex tw-cursor-pointer tw-items-center tw-gap-x-1 tw-border-0 tw-bg-transparent tw-text-[13px] tw-font-semibold tw-text-primary-400 tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-primary-400">{t22}{t23}</button>;
    $[62] = handleAddEntry;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== entries.length || $[65] !== firstEntry?.address) {
    t25 = entries.length === 1 && !firstEntry?.address && <p className="tw-mt-3 tw-text-xs tw-text-iron-500">Enter at least one wallet address to receive airdrops.</p>;
    $[64] = entries.length;
    $[65] = firstEntry?.address;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== t18 || $[68] !== t19 || $[69] !== t21 || $[70] !== t24 || $[71] !== t25) {
    t26 = <FormSection title="Airdrop Distribution">{t18}{t19}{t21}{t24}{t25}</FormSection>;
    $[67] = t18;
    $[68] = t19;
    $[69] = t21;
    $[70] = t24;
    $[71] = t25;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  return t26;
};
export default React.memo(AirdropConfig);
function _temp(sum, e) {
  return sum + (e.count || 0);
}